import { createStore, createEvent, createEffect, sample } from "effector";
import {
  GoalsState,
  GoalFilters,
  CreateGoalRequestType,
  UpdateGoalRequest,
  FundingGoal,
  MatchingCallsFilters,
  MatchingCallsData,
  FundingGoalEntityType,
} from "../model/types";
import { goalsService } from "../service";
import { CallFilters, CallResponse } from "@/features/calls/model/types";

export const fetchGoalsRequested = createEvent<GoalFilters | undefined>();
export const fetchGoalDetailRequested = createEvent<string>();
export const createGoalRequested = createEvent<CreateGoalRequestType>();
export const loadMoreMatchingCallsRequested = createEvent<
  MatchingCallsFilters | undefined
>();
export const updateGoalRequested = createEvent<{
  id: string;
  updates: UpdateGoalRequest;
}>();
export const deleteGoalRequested = createEvent<string>();
export const updateGoalAnalysis = createEvent<{
  goalId: string;
  analyzed: boolean;
}>();
export const setMatchingCallsSort = createEvent<string>();
export const fetchMatchingCallsRequested = createEvent<{
  goalId: string;
  filters?: MatchingCallsFilters;
  silent?: boolean;
}>();
export const analyzeCallRequested = createEvent<{
  goalId: string;
}>();
export const getEntityCallRequested = createEvent<{
  goalId: string;
}>();

export const fetchSingleGoalRequested = createEvent<{
  goalId: string;
}>();

export const fetchGoalsFx = createEffect(
  async (
    filters?: GoalFilters,
  ): Promise<{ count: number; data: FundingGoal[] }> => {
    return await goalsService.getAllGoals(filters);
  },
);

export const fetchSingleGoalFx = createEffect(
  async (goalId: string): Promise<FundingGoal> => {
    return await goalsService.getSingleGoal(goalId);
  },
);

export const analyzeCallFx = createEffect(
  async (
    goalId: string,
  ): Promise<{ status: string; nid: string; goalId: string }> => {
    return await goalsService.analyzeCall(goalId);
  },
);

export const getEntityCallFx = createEffect(
  async (goalId: string): Promise<FundingGoalEntityType> => {
    // console.log("🟢 getEntityCallFx effect called with goalId:", goalId);
    try {
      const result = await goalsService.getEntity(goalId);
      // console.log("🟢 getEntityCallFx SUCCESS, result:", result);
      return result;
    } catch (error) {
      console.error("🔴 getEntityCallFx FAILED:", error);
      throw error;
    }
  },
);

export const fetchMatchingCallsFx = createEffect(
  async ({
    goalId,
    filters,
  }: {
    goalId: string;
    filters?: MatchingCallsFilters;
  }): Promise<{
    total_counts: string;
    count: string;
    data: MatchingCallsData[];
  }> => {
    return await goalsService.getMatchingCalls(goalId, filters);
  },
);

export const loadMoreMatchingCallsFx = createEffect(
  async ({
    goalId,
    filters,
  }: {
    goalId: string;
    filters?: MatchingCallsFilters;
  }): Promise<{
    total_counts: string;
    count: string;
    data: MatchingCallsData[];
  }> => {
    return await goalsService.getMatchingCalls(goalId, filters);
  },
);

export const $goalsStore = createStore<GoalsState>({
  goals: [],
  selectedGoal: null,
  singleGoal: null,
  loading: false,
  error: null,
  loadMore: false,
  entity: undefined,
  calls_count: "",
  calls_total_count: "",
  status: "",
  count: 0,
  filters: {
    sort: "created",
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
  },
  matchingCalls: [],
  matchingCallsSort: "score_desc",
});

$goalsStore
  .on(fetchGoalsRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchGoalsFx.doneData, (state, response) => {
    // console.log("Goals data received in store:", response);
    return {
      ...state,
      goals: response.data,
      count: response.count,
      loading: false,
      error: null,
      pagination: {
        ...state.pagination,
        total: response.count,
        page: 1,
      },
    };
  })
  .on(fetchGoalsFx.failData, (state, error) => ({
    ...state,
    loading: false,
    error: error.message,
  }))
  .on(fetchGoalDetailRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchMatchingCallsRequested, (state, { silent }) => ({
    ...state,
    loading: !silent,
    error: null,
  }))
  .on(fetchMatchingCallsFx.doneData, (state, response) => ({
    ...state,
    matchingCalls: response.data,
    calls_total_count: response.total_counts,
    calls_count: response.count,
    loading: false,
    error: null,
    pagination: {
      ...state.pagination,
      page: 1,
    },
  }))
  .on(loadMoreMatchingCallsRequested, (state) => ({
    ...state,
    loadMore: true,
    error: null,
  }))
  .on(loadMoreMatchingCallsFx.doneData, (state, response) => ({
    ...state,
    matchingCalls: [...state.matchingCalls, ...response.data],
    calls_total_count: response.total_counts,
    calls_count: response.count,
    loadMore: false,
    error: null,
    pagination: {
      ...state.pagination,
      page: state.pagination.page + 1,
    },
  }))
  .on(fetchMatchingCallsFx.failData, (state, error) => ({
    ...state,
    loading: false,
    error: error.message,
  }))
  .on(analyzeCallRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(analyzeCallFx.doneData, (state, response) => ({
    ...state,
    status: response.status,
    loading: false,
    error: null,
  }))
  .on(getEntityCallRequested, (state) => {
    // console.log("🟡 getEntityCallRequested event received in store");
    return {
      ...state,
      loading: false,
      error: null,
    };
  })
  .on(getEntityCallFx.doneData, (state, response) => {
    // console.log("🟣 Store updating with entity:", response);
    return {
      ...state,
      entity: response,
      loading: false,
      error: null,
    };
  })
  .on(getEntityCallFx.failData, (state, error) => {
    console.error("🔴 Entity fetch failed in store:", error);
    return {
      ...state,
      loading: false,
      error: error.message,
    };
  })
  .on(setMatchingCallsSort, (state, newSort) => ({
    ...state,
    matchingCallsSort: newSort,
  }))
  .on(fetchMatchingCallsFx.failData, (state, error) => ({
    ...state,
    loading: false,
    error: error.message,
  }))
  .on(fetchSingleGoalRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchSingleGoalFx.doneData, (state, goal) => ({
    ...state,
    singleGoal: goal,
    loading: false,
    error: null,
  }))
  .on(fetchSingleGoalFx.failData, (state, error) => ({
    ...state,
    loading: false,
    error: error.message,
  }));

// Samples
sample({
  clock: fetchGoalDetailRequested,
  fn: (goalId) => ({ goalId }),
  target: fetchSingleGoalRequested,
});

sample({
  clock: fetchSingleGoalRequested,
  fn: ({ goalId }) => goalId,
  target: fetchSingleGoalFx,
});

sample({
  clock: fetchGoalsRequested,
  target: fetchGoalsFx,
});

sample({
  clock: fetchSingleGoalFx.doneData,
  filter: (goal) => goal.analyzed === "1",
  fn: (goal) => ({ goalId: goal.id }),
  target: fetchMatchingCallsRequested,
});

sample({
  clock: fetchMatchingCallsRequested,
  fn: ({ goalId, filters }) => ({ goalId, filters }),
  target: fetchMatchingCallsFx,
});

sample({
  clock: analyzeCallRequested,
  fn: ({ goalId }) => goalId,
  target: analyzeCallFx,
});

// ✅ CRITICAL CONNECTION
sample({
  clock: getEntityCallRequested,
  fn: ({ goalId }) => {
    // console.log("🔵 Sample triggered! Extracting goalId:", goalId);
    return goalId;
  },
  target: getEntityCallFx,
});

sample({
  clock: analyzeCallFx.doneData,
  filter: ({ status }) => status === "done",
  fn: ({ goalId }) => goalId,
  target: fetchGoalDetailRequested,
});

sample({
  clock: loadMoreMatchingCallsRequested,
  source: $goalsStore,
  filter: (state): state is GoalsState & { singleGoal: FundingGoal } =>
    state.singleGoal !== null,
  fn: (state, filters) => ({
    goalId: state.singleGoal!.id,
    filters: {
      ...filters,
      page: state.pagination.page + 1,
    },
  }),
  target: loadMoreMatchingCallsFx,
});
